import classNames from 'classnames';
import { useCopyTextToClipboard } from 'hooks/useCopyToClipboard';
import type { FC, MouseEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import type { QuoteWorkspaceType } from 'state/workspaces/model';
import { workspaceQuoteLoaded } from 'state/workspaces/workspace.actions';
import styled from 'styled-components';
import { getRootUrl } from 'utils/browser/browser';

import { QuoteRepriceIcon } from './QuoteRepriceIcon';

const CopyButton = styled.button`
  text-decoration: underline !important;
`;

export interface ExternalQuoteIdLinkProps {
  externalQuoteId: string;
  quoteId?: string;

  className?: string;
  linkClassName?: string;
  iconClassName?: string;
  targetWorkspace?: QuoteWorkspaceType;
  disabled?: boolean;
  canCopy?: boolean;
  canShare?: boolean;
  tabIndex?: number;
}

export const useGenerateUrl = (externalQuoteId: string) =>
  useCallback(() => `${getRootUrl()}#/quote/${externalQuoteId}`, [externalQuoteId]);

export const QuoteRepriceLink: FC<ExternalQuoteIdLinkProps> = ({
  quoteId,
  externalQuoteId,
  targetWorkspace,
  className,
  linkClassName,
  iconClassName,
  disabled,
  canCopy = true,
  canShare = false,
  tabIndex,
}) => {
  const { formatMessage } = useIntl();
  const [isHovered, setIsHovered] = useState(false);

  const generateUrl = useGenerateUrl(externalQuoteId);
  const generateSharableUrl = useCallback(() => `${getRootUrl()}#/sharable-quote/${quoteId}`, [quoteId]);

  const copyLinkToClipboard = useCopyTextToClipboard(
    'moreMenu.copyToClipboard.repriceLink.success',
    'moreMenu.copyToClipboard.repriceLink.error',
  );

  const copySharableLinkToClipboard = useCopyTextToClipboard(
    'moreMenu.copyToClipboard.sharableLink.success',
    'moreMenu.copyToClipboard.sharableLink.error',
  );

  const copyExternalQuoteIdToClipboard = useCopyTextToClipboard(
    'moreMenu.copyToClipboard.externalQuoteId.success',
    'moreMenu.copyToClipboard.externalQuoteId.error',
  );

  const dispatch = useDispatch();

  const onLinkClicked = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      dispatch(workspaceQuoteLoaded(externalQuoteId, quoteId, targetWorkspace));
      e.preventDefault();
      e.stopPropagation();
      return false;
    },
    [dispatch, externalQuoteId, quoteId, targetWorkspace],
  );

  const tooltip = useMemo(() => formatMessage({ id: 'moreMenu.copyToClipboard.repriceLink.tooltip' }), [formatMessage]);

  const sharableTooltip = useMemo(
    () => formatMessage({ id: 'moreMenu.copyToClipboard.sharableLink.tooltip' }),
    [formatMessage],
  );

  const externalQuoteIdTooltip = useMemo(
    () => formatMessage({ id: 'moreMenu.copyToClipboard.externalQuoteId.tooltip' }),
    [formatMessage],
  );

  const onMouseEnter = useCallback(() => setIsHovered(true), []);
  const onMouseLeave = useCallback(() => setIsHovered(false), []);

  return (
    <div
      className={classNames('d-flex flex-row align-items-center', className)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <CopyButton
        id="btnCopyExternalQuoteId"
        type="button"
        className={classNames('btn btn-link btn-sm', { invisible: !isHovered })}
        title={externalQuoteIdTooltip}
        onClick={() => copyExternalQuoteIdToClipboard(externalQuoteId)}
        tabIndex={tabIndex}
      >
        <FormattedMessage id="copyExternalQuoteId" />
      </CopyButton>
      {!disabled && canCopy && (
        <CopyButton
          id="btnCopyUrl"
          type="button"
          className={classNames('btn btn-link btn-sm', { invisible: !isHovered })}
          title={tooltip}
          onClick={() => copyLinkToClipboard(generateUrl())}
          tabIndex={tabIndex}
        >
          <FormattedMessage id="copyUrl" />
        </CopyButton>
      )}
      {canShare && (
        <CopyButton
          id="btnCopySharableUrl"
          type="button"
          className={classNames('btn btn-link btn-sm', { invisible: !isHovered })}
          title={sharableTooltip}
          onClick={() => copySharableLinkToClipboard(generateSharableUrl())}
          tabIndex={tabIndex}
        >
          <FormattedMessage id="copyShare" />
        </CopyButton>
      )}
      <span>{externalQuoteId}</span>
      {!disabled && (
        <QuoteRepriceIcon
          onLinkClicked={onLinkClicked}
          iconClassName={iconClassName}
          linkClassName={linkClassName}
          externalQuoteId={externalQuoteId}
        />
      )}
    </div>
  );
};
